import type {
  ImportCommitRequest,
  ImportCommitResponse,
  ImportPreviewRequest,
  ImportPreviewResponse,
} from '@study-decks/shared'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''

export class ImportApiError extends Error {
  override readonly name = 'ImportApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

async function requestImportApi<T>(path: string, idToken: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${idToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (response.status === 413) {
    throw new ImportApiError('PAYLOAD_TOO_LARGE', 'Input is too large (max 5 MB).')
  }

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    const code = errorBody.error?.code ?? 'REQUEST_FAILED'
    const message = resolveErrorMessage(code, errorBody.error?.message)
    throw new ImportApiError(code, message)
  }

  return (await response.json()) as T
}

function resolveErrorMessage(code: string, serverMessage: string | undefined): string {
  switch (code) {
    case 'NO_VALID_CARDS':
      return 'No valid cards found. Check your delimiters.'
    case 'IMPORT_PARTIAL_FAILURE':
      return "Some cards didn't save. The deck was created but is incomplete — check the deck and retry, or delete it."
    default:
      return serverMessage ?? `Request failed (${code})`
  }
}

export async function previewImport(
  idToken: string,
  body: ImportPreviewRequest,
): Promise<ImportPreviewResponse> {
  return requestImportApi<ImportPreviewResponse>('/api/import/quizlet/preview', idToken, body)
}

export async function commitImport(
  idToken: string,
  body: ImportCommitRequest,
): Promise<ImportCommitResponse> {
  return requestImportApi<ImportCommitResponse>('/api/import/quizlet/commit', idToken, body)
}
